/**
 * Access requests — user tracking + admin provision/reject queue
 * Layout aligned with GarageRequestsView (hero, tabs, stats).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  X,
  ChevronRight,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import config from '../../../config/environment';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import TourGuide from '../components/TourGuide';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import {
  ACCESS_REQUESTS_ADMIN_TOUR_STEPS,
  ACCESS_REQUESTS_USER_TOUR_STEPS,
} from '../tours/accessRequestsTour';

const REJECT_PRESETS = [
  'Not approved for your role or team.',
  'Please request access through your manager.',
  'Access is restricted to specific projects only.',
  'Requires security clearance review first.',
];

const statusBadgeClass = (status) => {
  if (status === 'pending') return 'garage-access-badge pending';
  if (status === 'provisioned') return 'garage-access-badge provisioned';
  if (status === 'rejected') return 'garage-access-badge rejected';
  return 'garage-access-badge';
};

const GarageAccessRequestsView = ({ isAdmin }) => {
  // Always open on My requests — Admin queue is a separate admin action surface
  const [activeTab, setActiveTab] = useState('my-requests');
  const [myRequests, setMyRequests] = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState(REJECT_PRESETS[0]);
  const [rejectCustom, setRejectCustom] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmProvision, setConfirmProvision] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  });

  const loadMine = useCallback(async () => {
    const data = await garageAPI.getMyAccessRequests();
    setMyRequests(data || []);
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    const params = statusFilter !== 'all' ? { status: statusFilter } : {};
    const data = await garageAPI.getAdminAccessRequests(params);
    setAdminRequests(data || []);
  }, [isAdmin, statusFilter]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadMine(), loadAdmin()]);
    } catch (err) {
      console.error('Failed to load access requests', err);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: err?.response?.data?.detail || 'Failed to load access requests',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [loadMine, loadAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!isAdmin && activeTab === 'admin-queue') {
      setActiveTab('my-requests');
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    setShowTour(false);
  }, [activeTab]);

  const myStats = useMemo(() => {
    const pending = myRequests.filter((r) => r.status === 'pending').length;
    const provisioned = myRequests.filter((r) => r.status === 'provisioned').length;
    const rejected = myRequests.filter((r) => r.status === 'rejected').length;
    return { pending, provisioned, rejected };
  }, [myRequests]);

  const adminStats = useMemo(() => {
    const all = adminRequests;
    return {
      pending: all.filter((r) => r.status === 'pending').length,
      provisioned: all.filter((r) => r.status === 'provisioned').length,
      rejected: all.filter((r) => r.status === 'rejected').length,
    };
  }, [adminRequests]);

  const handleProvision = async (request) => {
    setActionBusy(true);
    try {
      await garageAPI.provisionAccessRequest(request.id);
      setConfirmProvision(null);
      await refresh();
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Provision failed',
        message: err?.response?.data?.detail || 'Provision failed',
        variant: 'error',
      });
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const reason =
      rejectReason === '__custom__'
        ? rejectCustom.trim() || 'No reason provided.'
        : rejectReason;
    setActionBusy(true);
    try {
      await garageAPI.rejectAccessRequest(rejectTarget.id, reason);
      setRejectTarget(null);
      setRejectCustom('');
      await refresh();
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Reject failed',
        message: err?.response?.data?.detail || 'Reject failed',
        variant: 'error',
      });
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <GarageLoadingScreen message="Loading access requests…" />;
  }

  const stats = activeTab === 'admin-queue' ? adminStats : myStats;
  const isAdminQueue = activeTab === 'admin-queue';
  const wikiUrl = isAdminQueue
    ? config.ACCESS_REQUESTS_ADMIN_WIKI_URL
    : config.ACCESS_REQUESTS_USER_WIKI_URL;
  const wikiLabel = isAdminQueue ? 'Admin Tab Wiki' : 'User Tab Wiki';
  const tourSteps = isAdminQueue
    ? ACCESS_REQUESTS_ADMIN_TOUR_STEPS
    : ACCESS_REQUESTS_USER_TOUR_STEPS;
  const tourStorageKey = isAdminQueue
    ? 'tour-completed-access-requests-admin'
    : 'tour-completed-access-requests-user';

  return (
    <div className="garage-content garage-access-requests-view">
      <div className="garage-page-hero">
        <div>
          <h1 className="garage-page-title">Access requests</h1>
          <p className="garage-page-subtitle">
            {activeTab === 'admin-queue'
              ? 'Review pending access requests. Provision in Okta first, then mark provisioned here.'
              : 'Track the status of your app access requests from Discover.'}
          </p>
        </div>
        <div className="garage-hero-actions">
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={() => setShowTour(true)}
            title={`Take a quick tour of ${isAdminQueue ? 'Admin queue' : 'My requests'}`}
          >
            <Sparkles size={16} />
            Help
          </button>
          {wikiUrl && (
            <a
              className="garage-btn garage-btn-primary"
              href={wikiUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-tour="access-requests-wiki"
              title={`Open ${wikiLabel}`}
            >
              <ExternalLink size={16} />
              {wikiLabel}
            </a>
          )}
        </div>
      </div>

      <div className="garage-tabs garage-requests-tabs">
        <button
          type="button"
          className={`garage-tab ${activeTab === 'my-requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-requests')}
        >
          My requests
          {myRequests.length > 0 && (
            <span className="garage-tab-badge">{myRequests.length}</span>
          )}
        </button>
        {isAdmin && (
          <button
            type="button"
            className={`garage-tab ${activeTab === 'admin-queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin-queue')}
          >
            <Shield size={14} style={{ marginRight: 4 }} />
            Admin queue
            {adminStats.pending > 0 && (
              <span className="garage-tab-badge">{adminStats.pending}</span>
            )}
          </button>
        )}
      </div>

      <div className="garage-stats-grid" data-tour="access-requests-stats">
        <div className="garage-stat-card">
          <Clock className="garage-stat-icon" size={20} />
          <div className="garage-stat-content">
            <div className="garage-stat-number">{stats.pending}</div>
            <div className="garage-stat-label">Pending</div>
          </div>
        </div>
        <div className="garage-stat-card">
          <CheckCircle className="garage-stat-icon" size={20} />
          <div className="garage-stat-content">
            <div className="garage-stat-number">{stats.provisioned}</div>
            <div className="garage-stat-label">Provisioned</div>
          </div>
        </div>
        <div className="garage-stat-card">
          <AlertCircle className="garage-stat-icon" size={20} />
          <div className="garage-stat-content">
            <div className="garage-stat-number">{stats.rejected}</div>
            <div className="garage-stat-label">Rejected</div>
          </div>
        </div>
      </div>

      {activeTab === 'my-requests' && (
        <>
          {myRequests.length === 0 ? (
            <div className="garage-empty">
              <h3>No access requests yet</h3>
              <p>When you request access to an app from Discover, you can track it here.</p>
            </div>
          ) : (
            <div className="garage-access-my-list" data-tour="access-requests-user-list">
              {myRequests.map((req) => (
                <div key={req.id} className="garage-access-req-card">
                  <div className="garage-access-req-info">
                    <div className="garage-access-req-name">{req.displayName || 'App'}</div>
                    <div className="garage-access-req-sub">
                      Requested{' '}
                      {req.createdAt ? new Date(req.createdAt).toLocaleString() : '—'} ·{' '}
                      <span className={statusBadgeClass(req.status)}>{req.status}</span>
                    </div>
                    {req.rejectionReason && (
                      <div className="garage-access-req-reason">Reason: {req.rejectionReason}</div>
                    )}
                    <div className="garage-access-timeline">
                      <span className="done">Submitted</span>
                      <span className={req.status !== 'pending' ? 'done' : ''}>Under review</span>
                      <span
                        className={
                          req.status === 'provisioned'
                            ? 'done'
                            : req.status === 'rejected'
                              ? 'rejected'
                              : ''
                        }
                      >
                        Decision
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'admin-queue' && isAdmin && (
        <>
          <div className="garage-access-filter-bar" data-tour="access-requests-admin-filters">
            {['all', 'pending', 'provisioned', 'rejected'].map((f) => (
              <button
                key={f}
                type="button"
                className={`garage-access-filter ${statusFilter === f ? 'active' : ''}`}
                onClick={() => setStatusFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="garage-access-table-wrap" data-tour="access-requests-admin-queue">
            <table className="garage-access-table">
              <thead>
                <tr>
                  <th aria-label="Expand" />
                  <th>User</th>
                  <th>App</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="garage-access-empty-cell">
                      No requests in this category.
                    </td>
                  </tr>
                ) : (
                  adminRequests.map((req) => {
                    const expanded = expandedId === req.id;
                    const pending = req.status === 'pending';
                    return (
                      <React.Fragment key={req.id}>
                        <tr
                          className="garage-access-row"
                          onClick={() => setExpandedId(expanded ? null : req.id)}
                        >
                          <td>
                            <ChevronRight
                              size={16}
                              className={`garage-access-chevron ${expanded ? 'open' : ''}`}
                            />
                          </td>
                          <td>
                            <div className="garage-access-user-name">
                              {req.userName || req.userEmail}
                            </div>
                            <div className="garage-access-user-email">{req.userEmail}</div>
                          </td>
                          <td>{req.displayName}</td>
                          <td>
                            {req.createdAt ? new Date(req.createdAt).toLocaleString() : '—'}
                          </td>
                          <td>
                            <span className={statusBadgeClass(req.status)}>{req.status}</span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="garage-access-actions">
                              <button
                                type="button"
                                className="garage-btn garage-btn-sm garage-access-btn-provision"
                                disabled={!pending || actionBusy}
                                onClick={() => setConfirmProvision(req)}
                              >
                                <Check size={12} /> Provision
                              </button>
                              <button
                                type="button"
                                className="garage-btn garage-btn-sm garage-access-btn-reject"
                                disabled={!pending || actionBusy}
                                onClick={() => {
                                  setRejectTarget(req);
                                  setRejectReason(REJECT_PRESETS[0]);
                                  setRejectCustom('');
                                }}
                              >
                                <X size={12} /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="garage-access-detail-row">
                            <td colSpan={6}>
                              <div className="garage-access-detail-panel">
                                <p>
                                  <strong>User:</strong> {req.userName || '—'} &lt;{req.userEmail}
                                  &gt;
                                </p>
                                <p>
                                  <strong>Resource:</strong> {req.displayName} ({req.resourceType})
                                </p>
                                {req.rejectionReason && (
                                  <p>
                                    <strong>Rejection reason:</strong> {req.rejectionReason}
                                  </p>
                                )}
                                <p className="garage-access-phase2-note">
                                  Phase 1: assign the user in Okta (or upstream IAM), then click
                                  Provision. Phase 2 will auto-provision via API.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={!!confirmProvision}
        title="Mark as provisioned?"
        message={
          confirmProvision
            ? `Confirm you have already granted access for ${confirmProvision.userEmail} to ${confirmProvision.displayName} in Okta (or the upstream system).`
            : ''
        }
        confirmText={actionBusy ? 'Working…' : 'Mark provisioned'}
        cancelText="Cancel"
        onConfirm={() => confirmProvision && handleProvision(confirmProvision)}
        onCancel={() => !actionBusy && setConfirmProvision(null)}
      />

      {rejectTarget && (
        <div className="garage-modal-overlay" onClick={() => !actionBusy && setRejectTarget(null)}>
          <div
            className="garage-modal garage-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="garage-modal-header">
              <div className="garage-modal-title-row">
                <h2>Reject access request</h2>
              </div>
              <button
                type="button"
                className="garage-modal-close"
                onClick={() => setRejectTarget(null)}
                disabled={actionBusy}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="garage-modal-body">
              <p className="garage-confirm-message">
                Provide a reason. This will be visible to the user.
              </p>
              <select
                className="garage-access-reject-select"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              >
                {REJECT_PRESETS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                <option value="__custom__">Other (type below)</option>
              </select>
              {rejectReason === '__custom__' && (
                <textarea
                  className="garage-access-reject-text"
                  placeholder="Add details…"
                  value={rejectCustom}
                  onChange={(e) => setRejectCustom(e.target.value)}
                  rows={3}
                />
              )}
            </div>
            <div className="garage-modal-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                disabled={actionBusy}
                onClick={() => setRejectTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="garage-btn garage-btn-danger"
                disabled={actionBusy}
                onClick={handleReject}
              >
                {actionBusy ? 'Working…' : 'Confirm rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {showTour && (
        <TourGuide
          steps={tourSteps}
          storageKey={tourStorageKey}
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageAccessRequestsView;
